/**
 * ArchiveFormTemplateDialog — confirmation d'archivage (1c-i).
 *
 * L'archivage n'est PAS une suppression (décision PO §12) : soft, réversible
 * côté données (versions préservées), pas de restauration exposée en 1c-i. Le
 * bouton de confirmation est neutre/brand — jamais rouge destructif. Succès →
 * `onArchived()` (le parent invalide + rafraîchit l'état lecture seule). Focus
 * initial sur Annuler ; Escape/backdrop ferment (sauf pendant l'envoi).
 */
import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useArchiveFormTemplate } from "../../../lib/queries/formTemplates";
import { toast } from "../../../lib/toast";

type Props = {
  readonly templateId: string;
  readonly templateName: string;
  readonly onClose: () => void;
  readonly onArchived: () => void;
};

export function ArchiveFormTemplateDialog({
  templateId,
  templateName,
  onClose,
  onArchived,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const titleId = useId();
  const descId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  const archive = useArchiveFormTemplate();
  const { reset: resetMutation } = archive;

  useEffect(() => {
    resetMutation();
    queueMicrotask(() => cancelBtnRef.current?.focus());
  }, [resetMutation]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !archive.isPending) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, archive.isPending]);

  const onConfirm = () => {
    if (archive.isPending) return;
    archive.mutate(templateId, {
      onSuccess: () => {
        toast.success(t("form_templates.archive.success"));
        onArchived();
      },
    });
  };

  const apiErrorMsg =
    archive.error instanceof ApiException
      ? getApiErrorMessage(archive.error, lang) || t("errors.generic")
      : archive.isError
        ? t("errors.generic")
        : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => {
          if (!archive.isPending) onClose();
        }}
      />
      <div className="motion-safe:animate-fade-in relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <header className="border-b border-slate-100 px-5 py-4">
          <h2 id={titleId} className="text-base font-semibold text-slate-900">
            {t("form_templates.archive.title")}
          </h2>
        </header>

        <div className="space-y-3 px-5 py-5">
          <p id={descId} className="text-sm text-slate-700">
            {t("form_templates.archive.confirm", { name: templateName })}
          </p>
          <p className="text-sm text-slate-600">{t("form_templates.archive.note")}</p>

          {apiErrorMsg && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {apiErrorMsg}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={cancelBtnRef}
            type="button"
            onClick={onClose}
            disabled={archive.isPending}
            className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={archive.isPending}
            aria-busy={archive.isPending}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {archive.isPending && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
                <path
                  d="M14 8a6 6 0 0 1-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
            {archive.isPending
              ? t("form_templates.archive.submitting")
              : t("form_templates.archive.submit")}
          </button>
        </footer>
      </div>
    </div>
  );
}
